import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent,
  CardActions, Chip, IconButton, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Snackbar, Alert, Tooltip, CircularProgress, Fade, Skeleton,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Home as HomeIcon, Business as BusinessIcon,
  Refresh as RefreshIcon, GridView as GridViewIcon, TableRows as TableRowsIcon,
  SellOutlined as SellIcon, KeyOutlined as KeyIcon,
} from '@mui/icons-material';
import { propertyService } from '../services/propertyService';
import CsvImportModal from '../components/CsvImportModal';

const PROPERTY_TYPES = ['house', 'bungalow', 'flat', 'maisonette', 'land', 'commercial'];
const DEPARTMENTS = ['sales', 'lettings', 'management'];
const STATUS_OPTIONS = ['available', 'under_offer', 'sold', 'let', 'withdrawn', 'coming_soon'];

const STATUS_STYLES = {
  available: { label: 'Available', color: '#00C853', bg: '#E8F5E9' },
  under_offer: { label: 'Under Offer', color: '#FFB300', bg: '#FFF8E1' },
  sold: { label: 'Sold', color: '#D32F2F', bg: '#FFEBEE' },
  let: { label: 'Let', color: '#1976D2', bg: '#E3F2FD' },
  withdrawn: { label: 'Withdrawn', color: '#9E9E9E', bg: '#F5F5F5' },
  coming_soon: { label: 'Coming Soon', color: '#7B1FA2', bg: '#F3E5F5' },
};

const DEPT_ICONS = {
  sales: <SellIcon fontSize="small" />,
  lettings: <KeyIcon fontSize="small" />,
  management: <BusinessIcon fontSize="small" />,
};

const initialFormState = {
  property_type: 'house',
  status: 'available',
  department_type: 'sales',
  price: '',
  department: '',
  office: '',
  negotiator: '',
  address_line1: '',
  address_line2: '',
  address_line3: '',
  address_line4: '',
  postcode: '',
  country_code: 'GB',
  latitude: '',
  longitude: '',
  bedrooms: '',
  bathrooms: '',
  reception_rooms: '',
  floor_area: '',
  plot_size: '',
  parking: '',
  garage: '',
  garden: '',
  council_tax_band: '',
  epc_rating: '',
  strapline: '',
  description: '',
  summary: '',
  key_features: '',
  virtual_tour_url: '',
  viewing_times: '',
};

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptTab, setDeptTab] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProperty, setEditProperty] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formTab, setFormTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      let allResults = [];
      let pageNum = 1;
      let hasMore = true;

      while (hasMore) {
        const data = await propertyService.getAll({ page: pageNum });
        const results = Array.isArray(data) ? data : data.results || [];
        allResults = [...allResults, ...results];
        
        if (data && data.next) {
          pageNum++;
        } else {
          hasMore = false;
        }
        // Safety break
        if (pageNum > 100) hasMore = false;
      }
      setProperties(allResults);
    } catch {
      showSnackbar('Failed to load properties.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const getErrorProps = (name) => ({
    error: !!errors[name],
    helperText: errors[name] ? (Array.isArray(errors[name]) ? errors[name].join(', ') : errors[name]) : '',
  });

  const filteredProperties = properties.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch =
      (p.address_line1 || '').toLowerCase().includes(q) ||
      (p.town || '').toLowerCase().includes(q) ||
      (p.postcode || '').toLowerCase().includes(q) ||
      (p.property_type || '').toLowerCase().includes(q);
    const matchDept = deptTab === 'all' || p.department_type === deptTab;
    return matchSearch && matchDept;
  });

  const paged = filteredProperties.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleOpenCreate = () => {
    setEditProperty(null);
    setForm(initialFormState);
    setFormTab(0);
    setErrors({});
    setDialogOpen(true);
  };

  const handleOpenEdit = (prop) => {
    setEditProperty(prop);
    setForm({
      ...initialFormState,
      ...prop,
      country_code: prop.country_code || 'GB',
    });
    setFormTab(0);
    setErrors({});
    setDialogOpen(true);
  };

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
  };

  const handleSave = async () => {
    setErrors({});
    if (!form.address_line1.trim()) {
      setErrors({ address_line1: 'Address line 1 is required.' });
      showSnackbar('Please fix the highlighted errors.', 'error');
      setFormTab(1);
      return;
    }
    setSaving(true);
    
    // Sanitize payload
    const payload = { ...form };
    const intFields = ['bedrooms', 'bathrooms', 'reception_rooms'];
    intFields.forEach(f => {
      if (payload[f] === '') payload[f] = null;
      else if (payload[f] !== null) payload[f] = parseInt(payload[f], 10);
    });

    if (payload.price === '') payload.price = null;
    else if (payload.price !== null) payload.price = parseFloat(payload.price);
    
    const boolFields = ['garage', 'garden'];
    boolFields.forEach(f => {
      if (payload[f] === '') payload[f] = false;
      else if (typeof payload[f] === 'string') payload[f] = payload[f] === 'true';
    });

    try {
      if (editProperty) {
        await propertyService.update(editProperty.id, payload);
        showSnackbar('Property updated.');
      } else {
        await propertyService.create(payload);
        showSnackbar('Property created.');
      }
      setDialogOpen(false);
      fetchProperties();
    } catch (err) {
      let errMsg = 'Failed to save property.';
      if (err.response && err.response.data) {
        const data = err.response.data;
        if (typeof data === 'object') {
           setErrors(data);
           errMsg = 'Please fix the highlighted errors.';
        } else if (typeof data === 'string') {
           errMsg = data;
        }
      } else if (err.message) {
        errMsg = err.message;
      }
      showSnackbar(errMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await propertyService.delete(propertyToDelete.id);
      showSnackbar('Property deleted.');
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
      fetchProperties();
    } catch {
      showSnackbar('Failed to delete property.', 'error');
    }
  };

  const handleDeleteAllConfirm = async () => {
    setSaving(true);
    try {
      await propertyService.deleteAll();
      showSnackbar('All properties deleted successfully.');
      setDeleteAllOpen(false);
      fetchProperties();
    } catch {
      showSnackbar('Failed to delete all properties.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getPropertyLabel = (p) =>
    [p.address_line1, p.address_line2, p.postcode].filter(Boolean).join(', ') || 'Untitled Property';

  const getPropertyPrice = (p) => {
    if (p.price) {
      return `$${Number(p.price).toLocaleString()}`;
    }
    if (p.department_type === 'lettings' && p.letting_details?.rent) {
      return `$${p.letting_details.rent} ${p.letting_details.rent_frequency === 'weekly' ? 'pw' : 'pcm'}`;
    }
    if (p.department_type === 'sales' && p.selling_details?.price) {
      return `$${Number(p.selling_details.price).toLocaleString()}`;
    }
    return 'POA';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh', background: '#FAF7F3' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E', letterSpacing: '-0.5px' }}>
            Properties
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
            Manage your property listings across sales, lettings, and management.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchProperties} sx={{ border: '1px solid #E0E0E0', borderRadius: 2 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Grid view">
            <IconButton onClick={() => setViewMode('grid')} sx={{ border: `1px solid ${viewMode === 'grid' ? '#1B4A4D' : '#E0E0E0'}`, borderRadius: 2, color: viewMode === 'grid' ? '#1B4A4D' : '#888' }}>
              <GridViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Table view">
            <IconButton onClick={() => setViewMode('table')} sx={{ border: `1px solid ${viewMode === 'table' ? '#1B4A4D' : '#E0E0E0'}`, borderRadius: 2, color: viewMode === 'table' ? '#1B4A4D' : '#888' }}>
              <TableRowsIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            onClick={() => setDeleteAllOpen(true)}
            sx={{
              borderRadius: 2, px: 2, fontWeight: 600,
              borderColor: '#D32F2F', color: '#D32F2F',
              '&:hover': { borderColor: '#B71C1C', bgcolor: '#FFEBEE' },
            }}
          >
            Delete All
          </Button>
          <Button
            variant="outlined"
            onClick={() => setImportOpen(true)}
            sx={{
              borderRadius: 2, px: 2, fontWeight: 600,
              borderColor: '#1B4A4D', color: '#1B4A4D',
              '&:hover': { borderColor: '#113032', color: '#113032', bgcolor: 'rgba(27,74,77,0.04)' },
            }}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              background: '#1B4A4D',
              borderRadius: 2, px: 3, fontWeight: 600,
              boxShadow: '0 4px 15px rgba(27,74,77,0.3)',
              '&:hover': { background: '#113032' },
            }}
          >
            Add Property
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: properties.length, color: '#1B4A4D' },
          { label: 'Sales', value: properties.filter(p => p.department_type === 'sales').length, color: '#D84315' },
          { label: 'Lettings', value: properties.filter(p => p.department_type === 'lettings').length, color: '#018786' },
          { label: 'Management', value: properties.filter(p => p.department_type === 'management').length, color: '#7B1FA2' },
        ].map((stat) => (
          <Paper key={stat.label} elevation={0} sx={{
            p: 2, borderRadius: 3, border: '1px solid #E8EAF0',
            display: 'flex', alignItems: 'center', gap: 2, minWidth: 130,
          }}>
            <Box sx={{ width: 10, height: 36, background: stat.color, borderRadius: 2 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: stat.color, lineHeight: 1 }}>{stat.value}</Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>{stat.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #E8EAF0' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Search by address, town, postcode…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9E9E9E' }} /></InputAdornment>,
              sx: { borderRadius: 2 },
            }}
          />
          <Tabs value={deptTab} onChange={(_, v) => { setDeptTab(v); setPage(0); }} sx={{ minHeight: 'auto' }}>
            {['all', 'sales', 'lettings', 'management'].map((d) => (
              <Tab key={d} value={d} label={d.charAt(0).toUpperCase() + d.slice(1)}
                sx={{ minHeight: 36, py: 0.5, textTransform: 'none', fontWeight: 600, fontSize: 13 }} />
            ))}
          </Tabs>
        </Box>
      </Paper>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <Grid container spacing={2}>
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 3 }} />
              </Grid>
            ))
          ) : paged.length === 0 ? (
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px solid #E8EAF0' }}>
                <HomeIcon sx={{ fontSize: 64, color: '#E0E0E0', mb: 1 }} />
                <Typography sx={{ color: '#BDBDBD', fontWeight: 500 }}>No properties found</Typography>
                <Button onClick={handleOpenCreate} variant="outlined" size="small"
                  sx={{ mt: 2, borderRadius: 2, borderColor: '#1B4A4D', color: '#1B4A4D' }}>
                  Add your first property
                </Button>
              </Paper>
            </Grid>
          ) : paged.map((prop) => {
            const statusStyle = STATUS_STYLES[prop.status] || STATUS_STYLES.inactive;
            return (
              <Grid item xs={12} sm={6} md={4} key={prop.id}>
                <Fade in>
                  <Card elevation={0} sx={{
                    borderRadius: 3, border: '1px solid #E8EAF0', height: '100%',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    '&:hover': { boxShadow: '0 8px 30px rgba(27,74,77,0.12)', transform: 'translateY(-2px)' },
                  }}>
                    {/* Card top banner */}
                    <Box sx={{
                      height: 8, borderRadius: '12px 12px 0 0',
                      background: prop.department_type === 'sales'
                        ? 'linear-gradient(90deg, #1B4A4D, #4A7C7D)'
                        : prop.department_type === 'lettings'
                        ? 'linear-gradient(90deg, #018786, #00BFA5)'
                        : 'linear-gradient(90deg, #7B1FA2, #CE93D8)',
                    }} />
                    <CardContent sx={{ pt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Chip
                          icon={DEPT_ICONS[prop.department_type] || <HomeIcon fontSize="small" />}
                          label={prop.department_type || 'N/A'}
                          size="small"
                          sx={{ background: '#E8F5E9', color: '#1B4A4D', fontWeight: 600, fontSize: 11, textTransform: 'capitalize' }}
                        />
                        <Chip
                          label={statusStyle.label}
                          size="small"
                          sx={{ background: statusStyle.bg, color: statusStyle.color, fontWeight: 600, fontSize: 11 }}
                        />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1A1A2E', mb: 0.5 }}>
                        {getPropertyLabel(prop)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6B6360', textTransform: 'capitalize', mb: 1 }}>
                        {prop.property_type || 'Property'}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B4A4D', mt: 1 }}>
                        {getPropertyPrice(prop)}
                      </Typography>
                      {(prop.bedrooms || prop.bathrooms) && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          {prop.bedrooms && <Chip label={`${prop.bedrooms} bed`} size="small" sx={{ fontSize: 11 }} />}
                          {prop.bathrooms && <Chip label={`${prop.bathrooms} bath`} size="small" sx={{ fontSize: 11 }} />}
                        </Box>
                      )}
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'flex-end' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenEdit(prop)} sx={{ color: '#1B4A4D' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => { setPropertyToDelete(prop); setDeleteDialogOpen(true); }} sx={{ color: '#F44336' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E8EAF0', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#E8DDD0' }}>
                  {['Address', 'Type', 'Department', 'Status', 'Price', 'Beds', 'Baths', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1B4A4D', py: 2 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={36} sx={{ color: '#1B4A4D' }} /></TableCell></TableRow>
                ) : paged.length === 0 ? (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}><Typography color="#BDBDBD">No properties found</Typography></TableCell></TableRow>
                ) : paged.map((prop) => {
                  const statusStyle = STATUS_STYLES[prop.status] || STATUS_STYLES.inactive;
                  return (
                    <TableRow key={prop.id} hover sx={{ '&:hover': { background: '#F8F5FF' }, '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ fontWeight: 600, color: '#1A1A2E' }}>{getPropertyLabel(prop)}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize', color: '#555' }}>{prop.property_type || '—'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize', color: '#555' }}>{prop.department_type || '—'}</TableCell>
                      <TableCell>
                        <Chip label={statusStyle.label} size="small" sx={{ background: statusStyle.bg, color: statusStyle.color, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1B4A4D' }}>{getPropertyPrice(prop)}</TableCell>
                      <TableCell>{prop.bedrooms || '—'}</TableCell>
                      <TableCell>{prop.bathrooms || '—'}</TableCell>
                      <TableCell>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenEdit(prop)} sx={{ color: '#1B4A4D', mr: 0.5 }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => { setPropertyToDelete(prop); setDeleteDialogOpen(true); }} sx={{ color: '#F44336' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={filteredProperties.length} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[12, 24, 50]} sx={{ borderTop: '1px solid #F0F0F0' }}
          />
        </Paper>
      )}

      {/* Pagination for grid */}
      {viewMode === 'grid' && filteredProperties.length > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <TablePagination
            component="div" count={filteredProperties.length} page={page}
            onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[12, 24, 50]}
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: '#1B4A4D', color: '#fff', fontWeight: 700, py: 2.5 }}>
          {editProperty ? 'Edit Property' : 'New Property'}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Tabs value={formTab} onChange={(_, v) => setFormTab(v)} sx={{ borderBottom: '1px solid #E0E0E0', px: 2 }}>
            <Tab label="General" />
            <Tab label="Address" />
            <Tab label="Physical" />
            <Tab label="Marketing" />
          </Tabs>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {formTab === 0 && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField select label="Type" name="property_type" {...getErrorProps("property_type")} fullWidth size="small" value={form.property_type} onChange={handleFormChange}>
                      {PROPERTY_TYPES.map((t) => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField select label="Status" name="status" {...getErrorProps("status")} fullWidth size="small" value={form.status} onChange={handleFormChange}>
                      {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField select label="Department Type" name="department_type" {...getErrorProps("department_type")} fullWidth size="small" value={form.department_type} onChange={handleFormChange}>
                      {DEPARTMENTS.map((d) => <MenuItem key={d} value={d} sx={{ textTransform: 'capitalize' }}>{d}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Base Price" name="price" {...getErrorProps("price")} fullWidth size="small" type="number" value={form.price} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Department ID" name="department" {...getErrorProps("department")} fullWidth size="small" value={form.department} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Office ID" name="office" {...getErrorProps("office")} fullWidth size="small" value={form.office} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Negotiator ID" name="negotiator" {...getErrorProps("negotiator")} fullWidth size="small" value={form.negotiator} onChange={handleFormChange} />
                  </Grid>
                </>
              )}
              {formTab === 1 && (
                <>
                  <Grid item xs={12}>
                    <TextField label="Address Line 1 *" name="address_line1" {...getErrorProps("address_line1")} fullWidth size="small" value={form.address_line1} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Address Line 2" name="address_line2" {...getErrorProps("address_line2")} fullWidth size="small" value={form.address_line2} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Address Line 3" name="address_line3" {...getErrorProps("address_line3")} fullWidth size="small" value={form.address_line3} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Address Line 4" name="address_line4" {...getErrorProps("address_line4")} fullWidth size="small" value={form.address_line4} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Postcode" name="postcode" {...getErrorProps("postcode")} fullWidth size="small" value={form.postcode} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Country Code" name="country_code" {...getErrorProps("country_code")} fullWidth size="small" value={form.country_code} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Latitude" name="latitude" {...getErrorProps("latitude")} fullWidth size="small" type="number" value={form.latitude} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Longitude" name="longitude" {...getErrorProps("longitude")} fullWidth size="small" type="number" value={form.longitude} onChange={handleFormChange} />
                  </Grid>
                </>
              )}
              {formTab === 2 && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Bedrooms" name="bedrooms" {...getErrorProps("bedrooms")} fullWidth size="small" type="number" value={form.bedrooms} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Bathrooms" name="bathrooms" {...getErrorProps("bathrooms")} fullWidth size="small" type="number" value={form.bathrooms} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Reception Rooms" name="reception_rooms" {...getErrorProps("reception_rooms")} fullWidth size="small" type="number" value={form.reception_rooms} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Floor Area" name="floor_area" {...getErrorProps("floor_area")} fullWidth size="small" type="number" value={form.floor_area} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Plot Size" name="plot_size" {...getErrorProps("plot_size")} fullWidth size="small" type="number" value={form.plot_size} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Parking" name="parking" {...getErrorProps("parking")} fullWidth size="small" value={form.parking} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Garage" name="garage" {...getErrorProps("garage")} fullWidth size="small" value={form.garage === '' ? '' : String(form.garage)} onChange={handleFormChange}>
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Garden" name="garden" {...getErrorProps("garden")} fullWidth size="small" value={form.garden === '' ? '' : String(form.garden)} onChange={handleFormChange}>
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Council Tax Band" name="council_tax_band" {...getErrorProps("council_tax_band")} fullWidth size="small" value={form.council_tax_band} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="EPC Rating" name="epc_rating" {...getErrorProps("epc_rating")} fullWidth size="small" value={form.epc_rating} onChange={handleFormChange} />
                  </Grid>
                </>
              )}
              {formTab === 3 && (
                <>
                  <Grid item xs={12}>
                    <TextField label="Strapline" name="strapline" {...getErrorProps("strapline")} fullWidth size="small" value={form.strapline} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Summary" name="summary" {...getErrorProps("summary")} fullWidth size="small" multiline rows={2} value={form.summary} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Description" name="description" {...getErrorProps("description")} fullWidth size="small" multiline rows={4} value={form.description} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Key Features (JSON)" name="key_features" fullWidth size="small" multiline rows={2} value={form.key_features} onChange={handleFormChange} {...getErrorProps("key_features")} helperText={errors["key_features"] ? getErrorProps("key_features").helperText : "Enter as a JSON array"} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Virtual Tour URL" name="virtual_tour_url" {...getErrorProps("virtual_tour_url")} fullWidth size="small" value={form.virtual_tour_url} onChange={handleFormChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Open Times" name="viewing_times" {...getErrorProps("viewing_times")} fullWidth size="small" placeholder="e.g. Sat 10:00am - 10:30am" value={form.viewing_times} onChange={handleFormChange} />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2, color: '#666' }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            sx={{ borderRadius: 2, px: 4, background: '#1B4A4D', '&:hover': { background: '#113032' } }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : editProperty ? 'Save Changes' : 'Create Property'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Property</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{getPropertyLabel(propertyToDelete || {})}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} sx={{ borderRadius: 2, px: 3 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirm */}
      <Dialog open={deleteAllOpen} onClose={() => setDeleteAllOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#D32F2F' }}>Delete ALL Properties</DialogTitle>
        <DialogContent>
          <Typography>Are you absolutely sure you want to delete <strong>ALL</strong> properties? This action is permanent and cannot be undone!</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteAllOpen(false)} disabled={saving} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteAllConfirm} disabled={saving} sx={{ borderRadius: 2, px: 3 }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Yes, Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Modal */}
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={(count) => {
          showSnackbar(`Successfully imported ${count} properties!`, 'success');
          fetchProperties();
        }}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
